import * as React from 'react';
import { useState } from 'react';

import { useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';

import InformationBanner from 'bundles/onboarding-common/components/InformationBanner';
import type { RoleFragment } from 'bundles/onboarding-common/queries/__generated__/RoleFragment';
import UpdateCareerRoleMutation from 'bundles/unified-home-common/queries/UpdateCareerRoleMutation.graphql';
import type {
  UpdateCareerRoleMutation as UpdateCareerRoleMutationType,
  UpdateCareerRoleMutationVariables,
} from 'bundles/unified-home-common/queries/__generated__/UpdateCareerRoleMutation';

import _t from 'i18n!nls/onboarding-common';

type Props = {
  inferredRole: Pick<RoleFragment, 'name' | 'id'>;
  onConfirmRoleSuccess: () => void;
  onDismiss: () => void;
};

const InferredRoleModuleRenderer = ({ inferredRole, onConfirmRoleSuccess, onDismiss }: Props) => {
  const [isInferredRoleBannerDismissed, setIsInferredRoleBannerDismissed] = useState(false);
  const [isSettingCareerRole, setIsSettingCareerRole] = useState(false);
  const [updateCareerRoleMutation] = useMutation<UpdateCareerRoleMutationType, UpdateCareerRoleMutationVariables>(
    UpdateCareerRoleMutation,
    {
      context: { clientName: 'gatewayGql' },
      variables: {
        input: {
          occupationPreferences: {
            occupationPreferences: [
              {
                occupation: {
                  id: inferredRole.id,
                },
              },
            ],
          },
        },
      },
    }
  );

  const handleInferredRoleBannerDismissal = () => {
    setIsInferredRoleBannerDismissed(true);
    // TODO: Add logic to store banner dismissal in BE
    onDismiss();
  };

  const handleConfirmRole = async () => {
    setIsSettingCareerRole(true);
    try {
      const response = await updateCareerRoleMutation();
      const result = response.data?.LearnerProfile_PatchWorkPreferences;
      const error = result?.__typename === 'LearnerProfile_PatchFailure';

      if (error) {
        throw new Error(result.errors[0].message ?? 'Failed to update career role');
      }

      onConfirmRoleSuccess();
    } catch (error) {
      setIsSettingCareerRole(false);
      Sentry.captureException(error, {
        extra: {
          message: error.message,
        },
      });
    }
  };

  if (!inferredRole?.id) {
    return null;
  }

  return (
    <InformationBanner
      headerText={_t('Interested in #{role}?', { role: inferredRole.name })}
      bodyText={_t('Set it as your role and get personalized recommendations')}
      isDismissed={isInferredRoleBannerDismissed}
      onDismiss={handleInferredRoleBannerDismissal}
      ctaButton={{
        label: _t('Confirm role'),
        onClick: handleConfirmRole,
        isLoading: isSettingCareerRole,
      }}
    />
  );
};

export default InferredRoleModuleRenderer;
